// src/rms/pages/Timeline.jsx
// ============================================================================
// Timeline — the fleet, across time
// ============================================================================
// A row per unit, columns across, a bar per booking. It answers the one
// question no list can: where are the FREE days. A table shows what exists;
// absence has no row.
//
// Two rules hold this screen together.
//
// GRANULARITY IS DATA. Columns are sized from the vertical's
// rateDefaults.primaryUnit, so a bicycle shop gets hours and a car operator
// gets days out of the same component. Nothing branches on vertical_key.
//
// OCCUPANCY HAS ONE OWNER. What counts as "busy" comes from availability.js —
// its status list, its blackout predicate, its turnaround padding. A grid that
// decided for itself would drift from the engine that refuses the booking, and
// it would drift on the screen staff trust most and check least.
// ============================================================================

import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRms } from "../context/RmsContext";
import { useCollection } from "../hooks/useCollection";
import availability, { BLOCKING_RESERVATION_STATUSES, blockApplies } from "../pricing/availability";
import timeline from "../pricing/timeline";
import rentalService from "../data/rentalService";
import ReservationEditor from "../components/ReservationEditor";
import { PageHeader, EmptyState, Modal, Field, Select, TextInput, Banner, formatDateTime } from "../components/ui";
import { BLOCK_TYPES } from "./Availability";
import db from "../data";
import adEngine from "../ads/adEngine";

const POOL_COLLAPSE_ABOVE = 8;

function Timeline() {
  const { operator, config, t, tLower, isContinuous, invalidate, formatMoney, locale } = useRms();
  const navigate = useNavigate();

  const timeZone = operator?.timezone || "UTC";
  const primaryUnit = config?.rateDefaults?.primaryUnit || "day";
  const preferred = timeline.granularityFor(primaryUnit);

  const [unit, setUnit] = useState(preferred.unit);
  const [anchorMs, setAnchorMs] = useState(() => timeline.startOfDay(Date.now(), timeZone));

  // The unit decides the count, not the trade -- see COLUMN_COUNT. This used
  // to route a zoomed-out choice back through granularityFor, which maps a
  // RATE unit to a column unit, so picking "month" columns asked for the
  // month-rate default (26) and drew twenty-six months.
  const columnCount = timeline.columnCountFor(unit);
  const offeredUnits = useMemo(() => timeline.columnUnitsFor(primaryUnit), [primaryUnit]);

  const columns = useMemo(
    () => timeline.buildColumns({ anchorMs, unit, count: columnCount, timeZone }),
    [anchorMs, unit, columnCount, timeZone]
  );
  const bounds = useMemo(() => timeline.windowBounds(columns), [columns]);

  const { rows: assets } = useCollection("assets");
  const { rows: groups } = useCollection("assetGroups", {}, { orderBy: "position" });
  const { rows: customers } = useCollection("customers");
  const { rows: blocks } = useCollection("availabilityBlocks");

  // Only the bookings touching what is on screen. Padded by the largest
  // turnaround in the fleet, because a booking ending just before the window
  // still shades its opening hours.
  const maxTurnaround = useMemo(
    () => assets.reduce((n, a) => Math.max(n, a.turnaround_minutes || 0), 0),
    [assets]
  );
  const windowWhere = useMemo(
    () => availability.overlapWindow({
      startsAt: new Date(bounds.startMs).toISOString(),
      endsAt: new Date(bounds.endMs).toISOString(),
      paddingMinutes: maxTurnaround,
    }),
    [bounds.startMs, bounds.endMs, maxTurnaround]
  );
  const { rows: reservations } = useCollection("reservations", windowWhere);

  const customersById = useMemo(
    () => new Map(customers.map((c) => [c.id, c])),
    [customers]
  );
  const reservationsByAsset = useMemo(() => availability.byAsset(reservations), [reservations]);

  const customerName = (id) => {
    const c = customersById.get(id);
    return c ? `${c.first_name} ${c.last_name || ""}`.trim() : t("timeline.noCustomer", "—");
  };

  /** Everything occupying one unit in the window, as drawable spans. */
  const spansFor = useMemo(() => (asset) => {
    const buffer = (asset.turnaround_minutes || 0) * 60000;

    const booked = availability
      .reservationsFor(reservationsByAsset, asset.id)
      .filter((r) => BLOCKING_RESERVATION_STATUSES.includes(r.status))
      .map((r) => ({
        kind: "reservation",
        id: r.id,
        startMs: new Date(r.starts_at).getTime(),
        endMs: r.ends_at ? new Date(r.ends_at).getTime() : null,
        status: r.status,
        overdue: !!r.ends_at && new Date(r.ends_at).getTime() < Date.now() && r.status === "checked_out",
        label: customerName(r.customer_id),
        bufferMs: buffer,
        reservation: r,
      }));

    const blocked = blocks
      .filter((b) => blockApplies(b, asset))
      .map((b) => ({
        kind: "block",
        id: b.id,
        startMs: new Date(b.starts_at).getTime(),
        endMs: b.ends_at ? new Date(b.ends_at).getTime() : null,
        label: b.reason || t(`opt.blockType.${b.block_type}`, b.block_type || "blocked"),
      }));

    return [...booked, ...blocked];
  }, [reservationsByAsset, blocks, customersById]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- rows ---------------------------------------------------------------
  const groupedIds = useMemo(
    () => new Set(assets.filter((a) => a.group_id).map((a) => a.id)),
    [assets]
  );
  const standalone = useMemo(
    () => assets.filter((a) => !groupedIds.has(a.id) && a.status !== "retired"),
    [assets, groupedIds]
  );
  // Explicit choices only, keyed by group id. A Set of "expanded" ids cannot
  // represent "the operator closed this", and small pools open by default --
  // so adding to the set left them open, removing from it handed them back to
  // the default, and a pool of eight or fewer could never be collapsed at all.
  const [poolOverrides, setPoolOverrides] = useState(() => ({}));

  // ---- filters ------------------------------------------------------------
  // All four DIM rather than hide. A grid whose rows disappear when you type
  // stops being a picture of the fleet and becomes a search result, and the
  // thing an operator is looking for is usually the relationship between what
  // matched and what did not.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [poolFilter, setPoolFilter] = useState("");
  const [freeRun, setFreeRun] = useState("");

  const searchTerm = search.trim().toLowerCase();
  const runColumns = Math.max(0, Number(freeRun) || 0);

  const STATUS_GROUPS = {
    out: ["checked_out", "active"],
    upcoming: ["pending", "confirmed"],
  };

  const matchesFilters = (span) => {
    if (searchTerm && !String(span.label || "").toLowerCase().includes(searchTerm)) return false;
    if (!statusFilter) return true;
    if (statusFilter === "blocked") return span.kind === "block";
    if (span.kind === "block") return false;
    return (STATUS_GROUPS[statusFilter] || []).includes(span.status);
  };

  const filtersActive = !!(searchTerm || statusFilter);

  // ---- drag to create -----------------------------------------------------
  // Dragging picks a UNIT and a RANGE; what to do with them is asked
  // afterwards, because the same gesture is how an operator blocks a vehicle
  // for servicing and how they start a booking, and guessing wrong costs more
  // than one extra click.
  //
  // Snapped to whole columns. A drag is a pointer gesture with pixel precision
  // nobody wants: an operator dragging across Tuesday and Wednesday means those
  // two days, not 09:47 to 16:12.
  const [drag, setDrag] = useState(null);      // live gesture
  const [pending, setPending] = useState(null); // released, awaiting a choice
  const [blockDraft, setBlockDraft] = useState(null);

  // ---- drag to reschedule -------------------------------------------------
  // Only bookings that have not gone out yet. Moving a hire that is already
  // with the customer is a different act -- an extension, or a correction to
  // history -- and quietly re-pricing it from a drag would be the worst way to
  // offer either.
  const MOVABLE = ["pending", "confirmed"];
  const [moving, setMoving] = useState(null);   // live gesture on a bar
  const [proposal, setProposal] = useState(null); // engine's answer, awaiting a yes
  const [applying, setApplying] = useState(false);
  const [moveError, setMoveError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);

  /** Below this many pixels a gesture is a click, however it drifted. */
  const CLICK_SLOP_PX = 5;

  const startMove = (e, span, row) => {
    // Blocks move too. They have no price to re-quote, which makes them the
    // easier half of the same gesture -- and a bar an operator can see but not
    // drag reads as broken, not as protected.
    const movable = span.kind === "block"
      || (span.kind === "reservation" && MOVABLE.includes(span.status));
    if (!movable) return;

    e.stopPropagation();
    const track = e.currentTarget.closest(".rms-tl-track");
    const index = columnAt(track, e.clientX);
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* the move still works, it just ends if the pointer leaves the bar */
    }
    setMoving({ span, row, track, from: index, to: index, startX: e.clientX, movedPx: 0 });
  };

  const moveMove = (e) => {
    if (!moving) return;
    const index = columnAt(moving.track, e.clientX);
    const movedPx = Math.abs(e.clientX - moving.startX);
    setMoving((m) => (m ? { ...m, to: index, movedPx } : m));
  };

  const endMove = async () => {
    if (!moving) return;
    const { span, from, to, movedPx } = moving;
    setMoving(null);

    // Click, judged in PIXELS rather than columns. Judging by column made a
    // two-pixel wobble inside a wide column indistinguishable from a click,
    // and the click then navigated away -- which is why moving something
    // "did something really fast" and looked broken.
    if (movedPx < CLICK_SLOP_PX) {
      if (span.kind === "reservation") setEditing(span.reservation);
      return;
    }

    // A deliberate drag that landed back where it started is a cancelled
    // gesture. It stays put, silently, which is what dropping something back
    // in place should do.
    if (from === to) return;

    const shiftMs = columns[to].startMs - columns[from].startMs;
    const nextStart = new Date(span.startMs + shiftMs).toISOString();
    const nextEnd = span.endMs === null ? null : new Date(span.endMs + shiftMs).toISOString();

    setMoveError(null);

    if (span.kind === "block") {
      adEngine.recordAction();
      const moved = await db.update("availabilityBlocks", span.id, {
        starts_at: nextStart,
        ends_at: nextEnd,
      });
      if (!moved.success) setMoveError(moved.error);
      else setNotice(t("timeline.move.blockMoved", "{name} moved.", { name: span.label }));
      invalidate();
      return;
    }

    const result = await rentalService.previewReschedule(operator, span.id, nextStart, nextEnd);
    if (!result.success) {
      setMoveError(result.error);
      return;
    }
    setProposal(result.data);
  };

  const confirmMove = async () => {
    setApplying(true);
    adEngine.recordAction();
    const result = await rentalService.applyReschedule(
      operator,
      proposal.reservation.id,
      proposal.after.startsAt,
      proposal.after.endsAt
    );
    setApplying(false);
    if (!result.success) {
      setMoveError(result.error);
      return;
    }
    setProposal(null);
    setNotice(t("timeline.move.done", "Moved. The {booking} now runs {from} to {to}.", {
      booking: tLower("reservation", "reservation"),
      from: formatDateTime(proposal.after.startsAt),
      to: proposal.after.endsAt ? formatDateTime(proposal.after.endsAt) : "—",
    }));
    invalidate();
  };

  const moveGeometry = (span) => {
    if (!moving || moving.span.id !== span.id) return null;
    const shiftMs = columns[moving.to].startMs - columns[moving.from].startMs;
    return timeline.spanGeometry({
      startMs: span.startMs + shiftMs,
      endMs: span.endMs === null ? null : span.endMs + shiftMs,
      windowStartMs: bounds.startMs, windowEndMs: bounds.endMs,
    });
  };

  const columnAt = (track, clientX) => {
    const rect = track.getBoundingClientRect();
    const rtl = getComputedStyle(track).direction === "rtl";
    const offset = rtl ? rect.right - clientX : clientX - rect.left;
    const index = Math.floor((offset / rect.width) * columns.length);
    return Math.min(columns.length - 1, Math.max(0, index));
  };

  const startDrag = (e, row) => {
    // Only empty track. A pointerdown on a bar is a click on that booking.
    if (e.target.closest(".rms-tl-bar")) return;
    if (e.button !== undefined && e.button !== 0) return;
    const index = columnAt(e.currentTarget, e.clientX);
    // Capture keeps the gesture alive when the pointer leaves the row, which is
    // the normal case for a long drag. Guarded: a pointer that is no longer
    // active throws InvalidPointerId, and losing capture is survivable while
    // losing the whole gesture is not.
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* gesture still works, it just ends if the pointer leaves the row */
    }
    setDrag({ row, from: index, to: index, track: e.currentTarget });
  };

  const moveDrag = (e) => {
    if (!drag || e.currentTarget !== drag.track) return;
    // Resolved HERE, not inside the updater: React nulls currentTarget once the
    // handler returns, and a state updater runs after that. Reading it late
    // threw on every pointermove and took the whole grid down with it.
    const index = columnAt(e.currentTarget, e.clientX);
    setDrag((d) => (d ? { ...d, to: index } : d));
  };

  const endDrag = () => {
    if (!drag) return;
    const from = Math.min(drag.from, drag.to);
    const to = Math.max(drag.from, drag.to);
    setPending({
      row: drag.row,
      startMs: columns[from].startMs,
      endMs: columns[to].endMs,
    });
    setDrag(null);
  };

  const dragGeometry = (row) => {
    if (!drag || drag.row.id !== row.id) return null;
    const from = Math.min(drag.from, drag.to);
    const to = Math.max(drag.from, drag.to);
    return timeline.spanGeometry({
      startMs: columns[from].startMs, endMs: columns[to].endMs,
      windowStartMs: bounds.startMs, windowEndMs: bounds.endMs,
    });
  };

  const toLocalInput = (ms) => {
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const startBooking = () => {
    const p = pending;
    setPending(null);
    // A pool member is booked through its GROUP, never directly -- the booking
    // form deliberately omits grouped units, because the physical car is chosen
    // at checkout by least-recently-used. Handing over the member would arrive
    // at a dropdown that cannot select it.
    //
    // A blackout below does the opposite and targets the member, because it is
    // that specific vehicle going in for service.
    const params = new URLSearchParams({
      new: "1",
      target: p.row.groupId ? `group:${p.row.groupId}` : `asset:${p.row.id}`,
      starts: toLocalInput(p.startMs),
      ends: toLocalInput(p.endMs),
    });
    navigate(`/rms/reservations?${params.toString()}`);
  };

  const saveBlock = async () => {
    const d = blockDraft;
    adEngine.recordAction();
    await db.create("availabilityBlocks", {
      operator_id: operator.id,
      asset_id: d.row.id,
      asset_group_id: null,
      starts_at: new Date(d.startMs).toISOString(),
      ends_at: new Date(d.endMs).toISOString(),
      block_type: d.block_type,
      reason: d.reason || null,
      blocks_customer_booking: true,
      // Same rule as Settings → Availability: a soft hold keeps the unit
      // bookable by staff, everything else takes it off the road entirely.
      blocks_staff_booking: d.block_type !== "hold",
    });
    setBlockDraft(null);
    invalidate();
  };

  const poolRows = useMemo(
    () =>
      groups
        .map((g) => ({ group: g, members: assets.filter((a) => a.group_id === g.id && a.status !== "retired") }))
        .filter((r) => r.members.length),
    [groups, assets]
  );

  /** Open unless the operator said otherwise, or the pool is a big one. */
  const isExpanded = (id) =>
    Object.prototype.hasOwnProperty.call(poolOverrides, id)
      ? poolOverrides[id]
      : (poolRows.find((r) => r.group.id === id)?.members.length || 0) <= POOL_COLLAPSE_ABOVE;

  const toggle = (id) =>
    setPoolOverrides((s) => ({ ...s, [id]: !isExpanded(id) }));

  // Pool bookings with no unit chosen yet. The physical car is allocated at
  // checkout, so until then the commitment belongs to the pool, not a member.
  const unassignedFor = (groupId) =>
    availability
      .reservationsFor(reservationsByAsset, "unassigned")
      .filter((r) => r.asset_group_id === groupId && BLOCKING_RESERVATION_STATUSES.includes(r.status))
      .map((r) => ({
        kind: "unassigned",
        id: r.id,
        startMs: new Date(r.starts_at).getTime(),
        endMs: r.ends_at ? new Date(r.ends_at).getTime() : null,
        status: r.status,
        label: customerName(r.customer_id),
        reservation: r,
      }));

  /**
   * How many members of a pool are free at one instant. Instant, not window:
   * over two weeks nearly every unit is busy at some point, so a window-wide
   * count reports a healthy pool as a full one.
   */
  const freeAt = (members, atMs) =>
    members.filter((m) => !spansFor(m).some((s) => s.startMs <= atMs && (s.endMs ?? Infinity) > atMs)).length;

  const step = (direction) => {
    adEngine.recordAction();
    const span = bounds.endMs - bounds.startMs;
    setAnchorMs(timeline.startOfDay(anchorMs + direction * span, timeZone));
  };

  const nowMs = Date.now();
  const nowPct = nowMs >= bounds.startMs && nowMs <= bounds.endMs
    ? ((nowMs - bounds.startMs) / bounds.spanMs) * 100
    : null;

  /**
   * All four take the APP's locale, not `undefined`.
   *
   * `undefined` means the browser's language, which is a different thing and was
   * usually English: switching ReserveZoo to French left this header reading
   * "Fri" and "Sat" while every other word on the screen changed. It looked
   * hardcoded and was not -- it was localised to the wrong thing.
   *
   * `locale` comes from RmsContext, which subscribes to language changes, so
   * these rebuild the moment somebody switches and the header follows.
   */
  const dayFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { timeZone, weekday: "short", day: "numeric" }),
    [timeZone, locale]
  );
  const hourFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { timeZone, hour: "numeric" }),
    [timeZone, locale]
  );

  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { timeZone, month: "short", year: "2-digit" }),
    [timeZone, locale]
  );

  /**
   * The short month that goes under the date.
   *
   * "Three letter" is the English shape of this, not a rule -- Intl gives each
   * language its own short form, so this is Aug, août, авг, 8月, أغس. Truncating
   * to three characters would cut a Japanese month in half and mangle an Arabic
   * one, so the length is left to the locale.
   */
  const shortMonthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { timeZone, month: "short" }),
    [timeZone, locale]
  );

  /**
   * Where a column sits, measured from its own instants.
   *
   * Not from its index. Bars are placed from real milliseconds against the
   * window, and index-based columns only agreed with them while every column
   * was the same length. Months are 28 to 31 days, so a February column drawn
   * at one twelfth of the width sat several pixels away from a bar covering
   * February -- and the same arithmetic quietly mismatched the 23- and 25-hour
   * days of a DST changeover.
   */
  const columnGeometry = (col) => ({
    insetInlineStart: `${((col.startMs - bounds.startMs) / bounds.spanMs) * 100}%`,
    width: `${((col.endMs - col.startMs) / bounds.spanMs) * 100}%`,
  });

  /**
   * Only where the main label is a bare date.
   *
   * A month column already says which month it is, and an hour column is inside
   * one day -- repeating it there would be noise. The half-day column shows the
   * date on its morning half and "pm" on the other, so the month goes under the
   * morning only, where it belongs to something.
   */
  const columnSubLabel = (col) => {
    if (unit === "month" || unit === "hour") return null;
    if (unit === "half_day") {
      const h = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "numeric", hour12: false })
        .format(new Date(col.startMs));
      if (Number(h) >= 12) return null;
    }
    return shortMonthFormatter.format(new Date(col.startMs));
  };

  const columnLabel = (col) => {
    if (unit === "month") return monthFormatter.format(new Date(col.startMs));
    if (unit === "hour") return hourFormatter.format(new Date(col.startMs));
    if (unit === "half_day") {
      const h = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "numeric", hour12: false })
        .format(new Date(col.startMs));
      return Number(h) < 12 ? dayFormatter.format(new Date(col.startMs)) : t("timeline.pm", "pm");
    }
    return dayFormatter.format(new Date(col.startMs));
  };

  if (!assets.length) {
    return (
      <>
        <PageHeader title={t("nav.timeline", "Timeline")} />
        <EmptyState
          title={t("timeline.empty.title", "Nothing to show yet")}
          message={t("timeline.empty.body", "Add something to rent out and its bookings will appear here.")}
          action={
            <Link className="rms-btn rms-btn--primary" to="/rms/inventory?new=1">
              {t("dashboard.addAsset", "Add {item}", { item: tLower("assetSingular", "asset") })}
            </Link>
          }
        />
      </>
    );
  }

  const renderRow = (asset, indent = false) => {
    const spans = spansFor(asset);
    // Gaps are computed against the PADDED spans. A two-hour hole between two
    // hires with an hour of cleaning either side is not two free hours, and
    // offering it as one would be the screen contradicting the booking form.
    const gaps = timeline.gapsIn({
      busy: spans.map((s) => ({
        startMs: s.startMs - (s.bufferMs || 0),
        endMs: s.endMs === null ? null : s.endMs + (s.bufferMs || 0),
      })),
      windowStartMs: bounds.startMs,
      windowEndMs: bounds.endMs,
      minMs: timeline.columnMs(unit),
    });

    return (
      <div className="rms-tl-row" key={asset.id}>
        <div className={`rms-tl-label${indent ? " rms-tl-label--indent" : ""}`}>
          <Link to="/rms/inventory">{asset.name}</Link>
          {asset.asset_code ? <span className="rms-tl-code">{asset.asset_code}</span> : null}
        </div>
        <div
          className="rms-tl-track rms-tl-track--drag"
          onPointerDown={(e) => startDrag(e, { id: asset.id, name: asset.name, groupId: asset.group_id || null })}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={() => setDrag(null)}
        >
          {(() => {
            const geo = dragGeometry({ id: asset.id });
            return geo ? (
              <div
                className="rms-tl-drag"
                style={{ insetInlineStart: `${geo.leftPct}%`, width: `${geo.widthPct}%` }}
              />
            ) : null;
          })()}

          {gaps.map((g) => {
            const geo = timeline.spanGeometry({
              startMs: g.startMs, endMs: g.endMs,
              windowStartMs: bounds.startMs, windowEndMs: bounds.endMs,
            });
            if (!geo) return null;
            const cols = Math.round((g.endMs - g.startMs) / timeline.columnMs(unit));
            return (
              <div
                key={`gap-${g.startMs}`}
                className={`rms-tl-gap${runColumns && cols >= runColumns ? " is-hit" : ""}${
                  runColumns && cols < runColumns ? " is-dimmed" : ""
                }`}
                style={{ insetInlineStart: `${geo.leftPct}%`, width: `${geo.widthPct}%` }}
              >
                {geo.widthPct > 6 ? t("timeline.free", "{count} free", { count: cols }) : null}
              </div>
            );
          })}

          {/* Turnaround, drawn before the bars so a bar always sits on top of
              its own buffer. Until now this existed only as an error message
              AFTER the dates were chosen -- "needs 1 hour turnaround around
              RES-0042". Drawn, the hour is visible and nobody hits the error. */}
          {spans.filter((s) => s.bufferMs > 0).map((s) => {
            const geo = timeline.spanGeometry({
              startMs: s.startMs - s.bufferMs,
              endMs: s.endMs === null ? null : s.endMs + s.bufferMs,
              windowStartMs: bounds.startMs, windowEndMs: bounds.endMs,
            });
            if (!geo) return null;
            return (
              <div
                key={`buffer-${s.id}`}
                className="rms-tl-buffer"
                style={{ insetInlineStart: `${geo.leftPct}%`, width: `${geo.widthPct}%` }}
                title={t("timeline.turnaround", "{minutes} min turnaround either side", {
                  minutes: Math.round(s.bufferMs / 60000),
                })}
              />
            );
          })}

          {spans.map((s) => {
            const geo = timeline.spanGeometry({
              startMs: s.startMs, endMs: s.endMs,
              windowStartMs: bounds.startMs, windowEndMs: bounds.endMs,
            });
            if (!geo) return null;
            const tone = s.kind === "block" ? "block" : s.overdue ? "overdue" : s.status;
            const hit = matchesFilters(s);
            const movable = s.kind === "reservation" && MOVABLE.includes(s.status);
            const ghost = moveGeometry(s);
            return (
              <React.Fragment key={`${s.kind}-${s.id}`}>
              {ghost ? (
                <div
                  className="rms-tl-ghost"
                  style={{ insetInlineStart: `${ghost.leftPct}%`, width: `${ghost.widthPct}%` }}
                />
              ) : null}
              <button
                type="button"
                className={`rms-tl-bar rms-tl-bar--${tone}${geo.openEnded ? " rms-tl-bar--open" : ""}${
                  filtersActive && !hit ? " is-dimmed" : ""
                }${filtersActive && hit && searchTerm ? " is-hit" : ""}${movable ? " is-movable" : ""}${
                  moving && moving.span.id === s.id ? " is-moving" : ""
                }`}
                onPointerDown={(e) => startMove(e, s, asset)}
                onPointerMove={moveMove}
                onPointerUp={endMove}
                onPointerCancel={() => setMoving(null)}
                style={{ insetInlineStart: `${geo.leftPct}%`, width: `${geo.widthPct}%` }}
                title={`${s.label} · ${formatDateTime(new Date(s.startMs).toISOString())}${
                  s.endMs ? ` → ${formatDateTime(new Date(s.endMs).toISOString())}` : ""
                }`}
                onClick={() => {
                  // Pointer handlers already decided click-vs-drag; this only
                  // fires for a genuine click on something not draggable.
                  if (s.kind === "block") navigate("/rms/availability");
                }}
              >
                <span className="rms-tl-bar-text">{s.label}</span>
              </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title={t("nav.timeline", "Timeline")}
        subtitle={t("timeline.subtitle", "{count} {items} across {window}", {
          count: assets.length,
          items: tLower("assetPlural", "assets"),
          window: `${dayFormatter.format(new Date(bounds.startMs))} — ${dayFormatter.format(new Date(bounds.endMs - 1))}`,
        })}
        actions={
          <>
            <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => step(-1)}>
              ←
            </button>
            <button
              type="button"
              className="rms-btn rms-btn--ghost rms-btn--sm"
              onClick={() => setAnchorMs(timeline.startOfDay(Date.now(), timeZone))}
            >
              {t("timeline.today", "Today")}
            </button>
            <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => step(1)}>
              →
            </button>
            <select
              className="rms-input rms-input--sm"
              value={unit}
              onChange={(e) => { adEngine.recordAction(); setUnit(e.target.value); }}
              aria-label={t("timeline.zoom", "Column size")}
            >
              {offeredUnits.map((u) => (
                <option key={u} value={u}>
                  {t(`timeline.unit.${u}`, u.replace("_", " "))}
                </option>
              ))}
            </select>
          </>
        }
      />

      <div className="rms-tl-filters">
        <input
          className="rms-input rms-input--sm"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("timeline.filter.search", "Search {customer}", {
            customer: tLower("customer", "customer"),
          })}
          aria-label={t("timeline.filter.search", "Search {customer}", {
            customer: tLower("customer", "customer"),
          })}
        />

        <select
          className="rms-input rms-input--sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label={t("timeline.filter.status", "Status")}
        >
          <option value="">{t("timeline.filter.statusAll", "Everything")}</option>
          <option value="out">{t("timeline.filter.statusOut", "Out now")}</option>
          <option value="upcoming">{t("timeline.filter.statusUpcoming", "Not yet out")}</option>
          <option value="blocked">{t("timeline.filter.statusBlocked", "Blocked")}</option>
        </select>

        {poolRows.length ? (
          <select
            className="rms-input rms-input--sm"
            value={poolFilter}
            onChange={(e) => setPoolFilter(e.target.value)}
            aria-label={t("timeline.filter.pool", "Group")}
          >
            <option value="">{t("timeline.filter.poolAll", "All {items}", { items: tLower("assetPlural", "assets") })}</option>
            {poolRows.map(({ group }) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
            <option value="standalone">{t("timeline.filter.standalone", "Ungrouped only")}</option>
          </select>
        ) : null}

        {/* The counter question, asked directly: "sometime next week, about
            three days". Nothing else in the app can answer it -- every other
            screen needs the dates guessed first. */}
        <label className="rms-tl-run">
          {t("timeline.filter.findFree", "Find a free run of")}
          <input
            className="rms-input rms-input--sm"
            type="number"
            min="1"
            inputMode="numeric"
            value={freeRun}
            onChange={(e) => setFreeRun(e.target.value)}
            style={{ width: 62 }}
          />
          {t(`timeline.unit.${unit}`, unit.replace("_", " "))}
        </label>

        {filtersActive || runColumns ? (
          <button
            type="button"
            className="rms-linkish"
            onClick={() => { setSearch(""); setStatusFilter(""); setFreeRun(""); setPoolFilter(""); }}
          >
            {t("timeline.filter.clear", "Clear")}
          </button>
        ) : null}
      </div>

      <div className="rms-card rms-tl">
        <div className="rms-tl-scroll">
          <div className="rms-tl-inner" style={{ minWidth: `${Math.max(640, columns.length * (unit === "hour" ? 34 : 84))}px` }}>
            {/* 80 rather than 68 per column. The header is one date and one
                month per column in thirteen languages, and Arabic's short
                weekday is the full word -- "الجمعة، 21" wanted 56px in a 54px
                column and lost its last characters, and Bengali wanted 59.
                Sized by measuring all thirteen rather than by widening until
                English looked fine. The timeline scrolls horizontally on
                purpose, so the cost of a wider column is a scroll region that
                already exists. */}
            <div className="rms-tl-row rms-tl-row--head">
              <div className="rms-tl-label" />
              <div className="rms-tl-track">
                {columns.map((c) => (
                  <div key={c.startMs} className="rms-tl-col" style={columnGeometry(c)}>
                    <span className="rms-tl-col-day">{columnLabel(c)}</span>
                    {columnSubLabel(c) ? (
                      <span className="rms-tl-col-month">{columnSubLabel(c)}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            {nowPct !== null ? (
              <div className="rms-tl-now" style={{ insetInlineStart: `calc(200px + (100% - 200px) * ${nowPct / 100})` }} />
            ) : null}

            {poolRows.filter((r) => !poolFilter || poolFilter === r.group.id).map(({ group, members }) => {
              const open = isExpanded(group.id);
              const unassigned = unassignedFor(group.id);
              return (
                <React.Fragment key={group.id}>
                  <div className="rms-tl-row rms-tl-row--pool">
                    <div className="rms-tl-label">
                      {/* The caret sits in a gutter every label shares, not
                          inline in the text. Inline, it pushed the pool's name
                          a glyph-width right of every other name in the column,
                          so a collapsed pool did not line up with the standalone
                          rows beneath it.

                          Drawn rather than typed: ▾ and ▸ render at wildly
                          different weights across platforms and all but vanish
                          at this size. Horizontal direction DOES mirror, so the
                          collapsed arrow is flipped under RTL in CSS. */}
                      <button
                        type="button"
                        className="rms-tl-disclose"
                        onClick={() => toggle(group.id)}
                        aria-expanded={open}
                        aria-label={t("timeline.poolToggle", "Show or hide the {items} in {name}", {
                          items: tLower("assetPlural", "units"),
                          name: group.name,
                        })}
                      >
                        <svg
                          className={`rms-tl-caret${open ? " is-open" : ""}`}
                          width="13" height="13" viewBox="0 0 10 10"
                          aria-hidden="true" focusable="false"
                        >
                          <path d={open ? "M0.5 3 L9.5 3 L5 8.5 Z" : "M3 0.5 L8.5 5 L3 9.5 Z"} fill="currentColor" />
                        </svg>
                      </button>
                      <button type="button" className="rms-linkish rms-tl-pool-name" onClick={() => toggle(group.id)}>
                        {group.name}
                      </button>
                      <span className="rms-tl-code">
                        {t("timeline.poolFreeNow", "{free} of {total} free now", {
                          free: freeAt(members, nowMs),
                          total: members.length,
                        })}
                      </span>
                    </div>
                    <div className="rms-tl-track">
                      {/* Capacity per column, not one number for the whole
                          window. "2 of 3 free" is only meaningful for a
                          specific moment -- across a fortnight almost every
                          unit is busy at some point, which would report a busy
                          pool as an empty one. */}
                      {columns.map((c) => {
                        const free = freeAt(members, c.startMs);
                        return (
                          <div
                            key={`cap-${c.startMs}`}
                            className={`rms-tl-cap${free === 0 ? " rms-tl-cap--none" : ""}`}
                            style={{
                              ...columnGeometry(c),
                              opacity: members.length ? 0.15 + 0.55 * (1 - free / members.length) : 0,
                            }}
                            title={t("timeline.poolFreeAt", "{free} of {total} free", { free, total: members.length })}
                          >
                            {columns.length <= 16 ? free : null}
                          </div>
                        );
                      })}
                      {unassigned.map((s) => {
                        const geo = timeline.spanGeometry({
                          startMs: s.startMs, endMs: s.endMs,
                          windowStartMs: bounds.startMs, windowEndMs: bounds.endMs,
                        });
                        if (!geo) return null;
                        return (
                          <button
                            type="button"
                            key={s.id}
                            className="rms-tl-bar rms-tl-bar--unassigned"
                            style={{ insetInlineStart: `${geo.leftPct}%`, width: `${geo.widthPct}%` }}
                            title={t("timeline.unassigned", "{name} — unit assigned at checkout", { name: s.label })}
                            onClick={() => navigate("/rms/reservations")}
                          >
                            <span className="rms-tl-bar-text">{s.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {open ? members.map((m) => renderRow(m, true)) : null}
                </React.Fragment>
              );
            })}

            {(poolFilter && poolFilter !== "standalone" ? [] : standalone).map((a) => renderRow(a))}
          </div>
        </div>

        <div className="rms-tl-legend">
          <span><i className="rms-tl-key rms-tl-key--checked_out" />{t("timeline.legend.out", "Out")}</span>
          <span><i className="rms-tl-key rms-tl-key--confirmed" />{t("timeline.legend.booked", "Booked")}</span>
          <span><i className="rms-tl-key rms-tl-key--overdue" />{t("timeline.legend.overdue", "Overdue")}</span>
          <span><i className="rms-tl-key rms-tl-key--block" />{t("timeline.legend.blocked", "Blocked")}</span>
          <span><i className="rms-tl-key rms-tl-key--buffer" />{t("timeline.legend.turnaround", "Turnaround")}</span>
          <span><i className="rms-tl-key rms-tl-key--unassigned" />{t("timeline.legend.unassigned", "Unit not yet assigned")}</span>
          {isContinuous ? (
            <span>{t("timeline.legend.open", "A bar running off the edge has no end date.")}</span>
          ) : null}
        </div>
      </div>

      {pending ? (
        <Modal
          title={t("timeline.create.title", "{name} · {from} → {to}", {
            name: pending.row.name,
            from: formatDateTime(new Date(pending.startMs).toISOString()),
            to: formatDateTime(new Date(pending.endMs).toISOString()),
          })}
          onClose={() => setPending(null)}
          width={460}
          footer={
            <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setPending(null)}>
              {t("action.cancel", "Cancel")}
            </button>
          }
        >
          <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>
            {t("timeline.create.body", "What would you like to do with those dates?")}
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="rms-btn rms-btn--primary" onClick={startBooking}>
              {t("timeline.create.booking", "New {booking}", { booking: tLower("reservation", "reservation") })}
            </button>
            <button
              type="button"
              className="rms-btn rms-btn--ghost"
              onClick={() => {
                setBlockDraft({ ...pending, block_type: "maintenance", reason: "" });
                setPending(null);
              }}
            >
              {t("timeline.create.block", "Block it off")}
            </button>
          </div>
        </Modal>
      ) : null}

      {notice ? <Banner tone="ok">{notice}</Banner> : null}

      {editing ? (
        <ReservationEditor
          reservation={editing}
          customers={customers}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setNotice(msg); invalidate(); }}
        />
      ) : null}

      {moveError && !proposal ? (
        <Modal
          title={t("timeline.move.cannot", "That move is not possible")}
          onClose={() => setMoveError(null)}
          width={420}
          footer={
            <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setMoveError(null)}>
              {t("action.close", "Close")}
            </button>
          }
        >
          <Banner tone="danger">{moveError}</Banner>
        </Modal>
      ) : null}

      {proposal ? (
        <Modal
          title={t("timeline.move.title", "Move this {booking}?", {
            booking: tLower("reservation", "reservation"),
          })}
          onClose={() => { setProposal(null); setMoveError(null); }}
          width={520}
          footer={
            <>
              <button
                type="button"
                className="rms-btn rms-btn--ghost"
                onClick={() => { setProposal(null); setMoveError(null); }}
              >
                {t("action.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className="rms-btn rms-btn--primary"
                onClick={confirmMove}
                disabled={applying || proposal.blocked}
              >
                {t("timeline.move.confirm", "Move it")}
              </button>
            </>
          }
        >
          {moveError ? <Banner tone="danger">{moveError}</Banner> : null}
          {proposal.conflict ? <Banner tone="danger">{proposal.conflict.message}</Banner> : null}
          {!proposal.conflict && !proposal.quote.meets_minimum ? (
            <Banner tone="warn">
              {t("timeline.move.minimum", "A minimum of {count} {unit}s applies to those dates.", {
                count: proposal.quote.minimum_units,
                unit: proposal.quote.rate_unit.replace("_", " "),
              })}
            </Banner>
          ) : null}

          {/* Old against new, side by side. The price is the reason this dialog
              exists: a move across a weekend or into a busy fortnight re-prices
              legitimately, and a per-day add-on re-prices with it. Nothing here
              is written until the operator has seen both numbers. */}
          <div className="rms-tl-compare">
            <div>
              <div className="rms-label">{t("timeline.move.before", "Now")}</div>
              <div>{formatDateTime(proposal.before.startsAt)}</div>
              <div>{proposal.before.endsAt ? formatDateTime(proposal.before.endsAt) : "—"}</div>
              <strong>{formatMoney(proposal.before.totalCents)}</strong>
            </div>
            <div aria-hidden="true" className="rms-tl-compare-arrow">→</div>
            <div>
              <div className="rms-label">{t("timeline.move.after", "After the move")}</div>
              <div>{formatDateTime(proposal.after.startsAt)}</div>
              <div>{proposal.after.endsAt ? formatDateTime(proposal.after.endsAt) : "—"}</div>
              <strong
                className={
                  proposal.after.totalCents === proposal.before.totalCents
                    ? ""
                    : proposal.after.totalCents > proposal.before.totalCents
                      ? "rms-tl-up"
                      : "rms-tl-down"
                }
              >
                {formatMoney(proposal.after.totalCents)}
              </strong>
            </div>
          </div>

          {proposal.after.totalCents !== proposal.before.totalCents ? (
            <Banner tone="warn">
              {t("timeline.move.priceChanged", "The price changes by {delta} on those dates.", {
                delta: formatMoney(Math.abs(proposal.after.totalCents - proposal.before.totalCents)),
              })}
            </Banner>
          ) : null}

          {proposal.quote.rule_lines?.length ? (
            <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--rms-muted-strong)" }}>
              {t("timeline.move.rules", "Applied:")}{" "}
              {proposal.quote.rule_lines.map((r) => r.name).join(" · ")}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {blockDraft ? (
        <Modal
          title={t("timeline.block.title", "Block off {name}", { name: blockDraft.row.name })}
          onClose={() => setBlockDraft(null)}
          width={460}
          footer={
            <>
              <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setBlockDraft(null)}>
                {t("action.cancel", "Cancel")}
              </button>
              <button type="button" className="rms-btn rms-btn--primary" onClick={saveBlock}>
                {t("action.save", "Save")}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 13, color: "var(--rms-muted-strong)", marginTop: 0 }}>
            {formatDateTime(new Date(blockDraft.startMs).toISOString())} →{" "}
            {formatDateTime(new Date(blockDraft.endMs).toISOString())}
          </p>
          <Field label={t("availability.type", "Type")}>
            <Select
              value={blockDraft.block_type}
              onChange={(v) => setBlockDraft((d) => ({ ...d, block_type: v }))}
              options={BLOCK_TYPES.map((b) => ({ value: b.value, label: t(b.labelKey, b.label) }))}
            />
          </Field>
          <Field
            label={t("availability.reason", "Reason")}
            help={t("timeline.block.reasonHelp", "Shown on the bar, so staff know why it is off the road.")}
          >
            <TextInput
              value={blockDraft.reason}
              onChange={(v) => setBlockDraft((d) => ({ ...d, reason: v }))}
            />
          </Field>
        </Modal>
      ) : null}
    </>
  );
}

export default Timeline;
